import { createFileRoute } from '@tanstack/react-router';
import { LegalPageFrame } from '~/components/sections/LegalDocument';
import { privacyContent, type PrivacyBlock } from '~/content/privacy';
import { pageHeadMeta } from '~/lib/seo';
import readingStylesHref from '~/styles/reading.css?url';

export const Route = createFileRoute('/privacy-policy')({
  head: () =>
    pageHeadMeta({
      title: 'Privacy – nsave',
      description:
        'Learn how nsave collects, processes, and protects your personal data. Understand your privacy rights and our commitment to data security.',
      path: '/privacy-policy',
      stylesheets: [readingStylesHref],
    }),
  component: PrivacyPolicyPage,
});

function Block({ block }: { block: PrivacyBlock }) {
  switch (block.type) {
    case 'paragraph':
      return <p className="text-body text-grey-900">{block.text}</p>;
    case 'subheading':
      return <p className="text-body font-semibold text-black">{block.text}</p>;
    case 'list':
      return (
        <ul className="flex list-disc flex-col gap-3 pl-5 text-body text-grey-900 marker:text-grey-500">
          {block.items.map((item) => (
            <li key={item} className="pl-1">
              {item}
            </li>
          ))}
        </ul>
      );
    case 'table': {
      const isSingleColumn = block.columns.length === 1;
      return (
        <table className="w-full border-collapse text-left text-body text-grey-900">
          <thead>
            <tr className="border-b border-black-6">
              {block.columns.map((column) => (
                <th key={column} className="py-3 pr-6 align-top font-semibold text-black last:pr-0">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row) => (
              <tr key={row.join('|')} className="border-b border-black-6 last:border-b-0">
                {row.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    className={
                      isSingleColumn
                        ? 'py-3 align-top'
                        : cellIndex === 0
                          ? 'w-[38%] py-3 pr-6 align-top font-medium text-black'
                          : 'py-3 align-top'
                    }>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
  }
}

function PrivacyPolicyPage() {
  return (
    <LegalPageFrame>
      <h1 className="text-h3 font-semibold tracking-[-0.03em] text-balance md:text-h2">
        {privacyContent.title}
      </h1>

      <div className="mt-10 max-w-[640px] space-y-6">
        {privacyContent.intro.map((paragraph) => (
          <p key={paragraph} className="text-body text-grey-900">
            {paragraph}
          </p>
        ))}
      </div>

      <dl className="mt-10 max-w-[640px] space-y-2">
        {privacyContent.controller.map((line) => (
          <div key={line.label} className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
            <dt className="text-body font-semibold text-black">{line.label}:</dt>
            <dd className="text-body text-grey-900">{line.value}</dd>
          </div>
        ))}
      </dl>

      <div className="mt-[96px] space-y-[84px]">
        {privacyContent.sections.map((section) => (
          <section key={section.number}>
            <h2 className="text-h5 font-semibold tracking-[-0.02em] md:text-h4">
              {section.number}. {section.title}
            </h2>
            <div className="mt-10 space-y-6">
              {section.body.map((block, index) => (
                <Block key={index} block={block} />
              ))}
            </div>
          </section>
        ))}

        <section>
          <h2 className="text-h5 font-semibold tracking-[-0.02em] md:text-h4">
            {privacyContent.closing.title}
          </h2>
          <dl className="mt-10 max-w-[640px] space-y-2">
            {privacyContent.closing.lines.map((line) => (
              <div key={line.label} className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
                <dt className="text-body font-semibold text-black">{line.label}:</dt>
                <dd className="text-body text-grey-900">{line.value}</dd>
              </div>
            ))}
          </dl>
        </section>
      </div>
    </LegalPageFrame>
  );
}
